import { useCallback, useMemo } from "react";
import {
  Alert,
  AlertAction,
  AlertDescription,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Separator,
  useIsMobile,
} from "@salestrak/ui";
import { DetailViewTabsList } from "#app/components/detail-view/detail-view-tabs";
import { AccessMarker, EntityBreadcrumbLink } from "#app/components/entity";
import { ProjectFollowUpAction } from "#app/components/updates/project-follow-up-action";
import type { IssueRow } from "#app/lib/data";
import { useIssueCardActions } from "#app/hooks/kanban/use-issue-card-actions";
import { useTrashMutations } from "#app/hooks/settings/use-trash-mutations";
import { useAppData } from "#app/lib/data";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkflowId } from "#app/lib/hooks/use-workflow-id";
import {
  DETAIL_SUMMARY_PINNED,
  useDetailViewTabOrder,
} from "#app/lib/hooks/use-view-tab-order";
import { useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { useIssueViewOptional } from "./use-issue-view";
import {
  GUEST_ISSUE_VIEW_TABS,
  ISSUE_VIEW_TABS,
  type IssueViewStage,
  type IssueViewTab,
} from "./issue-view-types";
import type { IconName, Omittable } from "@salestrak/ui";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

type IssueBreadcrumbProps = Omittable<{
  issue?: IssueRow;
}>;

export function IssueBreadcrumb({ issue }: IssueBreadcrumbProps) {
  const { workflows } = useAppData();
  const createCtx = useCreateIssueMode();
  const issueView = useIssueViewOptional();
  const { push } = useAppNavigate();
  const isCreateMode = !!createCtx;

  const workflowId = isCreateMode
    ? createCtx.draft.workflowId
    : issue?.workflowId;

  const workflow = useMemo(
    () => workflows.find((wf) => wf.workflowId === workflowId),
    [workflows, workflowId],
  );

  const activeWorkflowId = useWorkflowId();

  const handleWorkflowClick = useCallback(() => {
    if (!workflowId) return;
    issueView?.closeIssue();
    // When the underlying route is already this workflow (any tab or saved
    // view), closing the overlay IS the navigation — a push would bounce the
    // user off their chosen view to the workflow default and stack a history
    // entry for the page they were already on.
    if (activeWorkflowId !== workflowId) push(`/workflow/${workflowId}`);
  }, [workflowId, activeWorkflowId, issueView, push]);

  return (
    // The trail and the access marker share one row. The trail truncates its
    // page segment; the marker yields its label before that happens
    // (`useFitsBeside` reads this row), so a long name always wins the width.
    <Box direction="row" align="center" gap="sm" minWidth={0}>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            {/* Icon-only on purpose (pv2's drawer treatment, adopted for every
                presentation): the workflow reads as a glyph, its name on a
                hover tooltip, ceding the trail to the project name. */}
            <EntityBreadcrumbLink
              entity={{
                kind: "workflow",
                id: workflowId ?? "",
                workflow: workflow ?? { name: "Workflow" },
              }}
              onClick={isCreateMode ? undefined : handleWorkflowClick}
              collapsed
            />
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            {/* Raw string on purpose: BreadcrumbPage pins the trail's 14px and
                owns truncation — a nested Text block re-rolls both and used to
                ride above the centerline. */}
            <BreadcrumbPage>
              {isCreateMode ? "New Project" : (issue?.name ?? "")}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      {/* Why the fields around it are inert. A draft has no per-object tier,
          so create mode carries none. */}
      {issue && !isCreateMode ? (
        <AccessMarker
          level={issue.viewerAccessLevel}
          canEdit={issue.viewerCanEdit}
        />
      ) : null}
    </Box>
  );
}

// The sortable tabs' visuals — Summary is the pinned anchor, rendered
// separately, so it has no row here.
const TAB_META: Record<
  Exclude<IssueViewTab, "summary">,
  { icon: IconName; label: string }
> = {
  forms: { icon: "clipboard-check", label: "Forms" },
  activity: { icon: "activity", label: "Activity" },
  media: { icon: "image", label: "Attachments" },
};

type IssueViewHeaderProps = Omittable<{
  issue?: IssueRow;
  /** The body's resolved Stage — the follow-up verb reads the Stage's cycle
   *  pin off it rather than resolving a second time. */
  stage?: IssueViewStage | null;
  /** The mounted tab — the strip's "More" trigger lights while it is folded.
   *  Create mode renders no strip, so the pair is optional. */
  activeTab?: IssueViewTab;
  /** Activates a tab picked from the strip's "More" menu. */
  onSelectTab?: (tab: IssueViewTab) => void;
  /** The body's content column, so the archived banner lines up with it. */
  contentMaxWidth?: number;
  showFormsTab?: boolean;
  /** The strip's trailing action (the follow-up verb) stands down: the body
   *  under the strip is not the Project right now — the layout editor is —
   *  and a verb that acts on the Project beside an editor that does not
   *  reads as part of the editor. */
  hideActions?: boolean;
}>;

export function IssueViewHeader({
  issue,
  stage,
  activeTab,
  onSelectTab,
  showFormsTab,
  contentMaxWidth,
  hideActions = false,
}: IssueViewHeaderProps) {
  const createCtx = useCreateIssueMode();
  const { unarchiveIssue } = useIssueCardActions();
  const { restoreIssue } = useTrashMutations();
  const isCreateMode = !!createCtx;
  const isMobile = useIsMobile();
  // The phone sheet's one content edge: the header's 24px band, which the
  // summary's stack gutter also lands on. Desktop keeps the dialog's `md`.
  const stripInset = isMobile ? "lg" : "md";

  // Per-user drag order for everything after Summary, which stays pinned
  // first: its value is left out of the row's `order`, so it is no drag
  // source and no drop target.
  const shareOnly = useViewerIsShareOnly();
  const { order, reorderVisible } = useDetailViewTabOrder(
    "project",
    shareOnly ? GUEST_ISSUE_VIEW_TABS : ISSUE_VIEW_TABS,
    DETAIL_SUMMARY_PINNED,
  );
  // Summary is the pinned anchor and never sorts; the body decides whether
  // the issue has a Forms tab at all.
  const sortableTabs = useMemo(
    () =>
      order.filter(
        (tab): tab is Exclude<IssueViewTab, "summary"> =>
          tab !== "summary" && !(tab === "forms" && showFormsTab === false),
      ),
    [order, showFormsTab],
  );

  const handleUnarchive = useCallback(async () => {
    if (!issue) return;
    await unarchiveIssue(issue);
  }, [issue, unarchiveIssue]);

  const handleRestore = useCallback(async () => {
    if (!issue) return;
    await restoreIssue(issue.issueId);
  }, [issue, restoreIssue]);

  return (
    <Box>
      {!isCreateMode && (
        // No paddingTop: the header row's own paddingY="sm" (8px) already
        // supplies the gap, matching the workflow route's 8px between its
        // first line and tab strip. Any extra here pushes this separator
        // below the workflow route's border whenever the drawer sits beside
        // a list — the block is 8 + 28 + 8 + 36 = 80px on both.
        <Box paddingX={stripInset}>
          <DetailViewTabsList
            activeTab={activeTab ?? "summary"}
            meta={TAB_META}
            onOrderChange={(...args: Parameters<typeof reorderVisible>) => {
              trackEvent({ context: AnalyticsContext.Issue, event: IssueEvent.ReorderDetailTabs });
              reorderVisible(...args);
            }}
            onSelectTab={(tab) => onSelectTab?.(tab)}
            sortableTabs={sortableTabs}
            actions={
              issue && !hideActions ? (
                <ProjectFollowUpAction
                  editable={issue.viewerCanEdit}
                  issue={issue}
                  stage={stage}
                />
              ) : undefined
            }
          />
        </Box>
      )}

      <Separator />

      {/* The trash outranks the archive: a trashed Project is read-only
          (the server refuses its edits) until it is restored, and an
          archived one that is also trashed comes back still archived.
          Restore rides the self-serve path, the same gate as the delete. */}
      {!isCreateMode && issue?.trashedAt ? (
        <Box paddingX={stripInset} paddingTop="md" align="center">
          <Box fill="width" maxWidth={contentMaxWidth}>
            <Alert variant="warning">
              <AlertDescription>
                This Project is in the trash. Restore it to make changes.
              </AlertDescription>
              {issue.viewerCanDelete ? (
                <AlertAction>
                  <Button variant="link" size="sm" onClick={handleRestore}>
                    Restore
                  </Button>
                </AlertAction>
              ) : null}
            </Alert>
          </Box>
        </Box>
      ) : !isCreateMode && issue?.archived && (
        <Box paddingX={stripInset} paddingTop="md" align="center">
          <Box fill="width" maxWidth={contentMaxWidth}>
            <Alert variant="warning">
              <AlertDescription>This Project has been archived.</AlertDescription>
              <AlertAction>
                <Button variant="link" size="sm" onClick={handleUnarchive}>
                  Unarchive
                </Button>
              </AlertAction>
            </Alert>
          </Box>
        </Box>
      )}
    </Box>
  );
}
